import { isUnassignedRow, type ScheduleJobRow } from './job-rows'

/**
 * Who can take which job. One pure rule set shared by the week grid, the day timeline, the side
 * panel and the stat cards, so they never disagree.
 */

/** A worker's availability on one day. `unset` = no availability saved for that day (hours rule skipped). */
export type WorkerDayStatus =
  | { kind: 'shift'; ranges: Array<[number, number]>; label: string }
  | { kind: 'unset' }
  | { kind: 'off' }
  | { kind: 'leave'; leaveType: string | null }

export interface ScheduleWorker {
  id: string
  name: string
  /** Category names the worker can do (display spelling). */
  categoryNames: string[]
  /** A skill without a category: the worker can do every category. */
  anyCategory: boolean
  /** False when the worker's categories could not be read: the category rule is skipped. */
  categoriesKnown: boolean
  /** Keyed by YYYY-MM-DD; a missing day is `unset`. */
  days: Record<string, WorkerDayStatus>
}

export type CheckKind = 'ok' | 'leave' | 'off' | 'category' | 'hours' | 'clash'

export interface CheckResult {
  ok: boolean
  kind: CheckKind
  /** Full reason, always shown next to colour (e.g. "On leave (Sick leave)"). */
  text: string
}

/** Short reason for dimmed grid cells / lanes. */
export const SHORT_REASON: Record<Exclude<CheckKind, 'ok'>, string> = {
  leave: 'On leave',
  off: 'Day off',
  category: 'Category',
  hours: 'Hours',
  clash: 'Clash',
}

const OK: CheckResult = { ok: true, kind: 'ok', text: 'Free' }
/** Completed jobs are read only: nothing is validated for them. */
const COMPLETED: CheckResult = { ok: true, kind: 'ok', text: 'Completed' }

export const formatClock = (minute: number) => `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`

export const formatMinuteRange = (startMinute: number, endMinute: number) => `${formatClock(startMinute)}-${formatClock(endMinute)}`

export const dayStatusOf = (worker: ScheduleWorker, day: string): WorkerDayStatus => worker.days[day] ?? { kind: 'unset' }

const normalize = (value: string | null | undefined) => String(value || '').trim().toLowerCase()

export const hasWorker = (job: ScheduleJobRow, workerId: string) => job.workers.some((worker) => worker.workerId === workerId)

const hasWindow = (job: ScheduleJobRow): job is ScheduleJobRow & { startMinute: number; endMinute: number } => job.startMinute != null && job.endMinute != null

/**
 * Can `worker` take `job`? Rules in order: leave, day off, category, shift hours, clash with
 * another job of the worker that day (completed jobs count as that other job). A completed
 * `job` itself is never validated. `sameDayJobs` may hold any jobs; only those on the job's
 * day are used. The job itself (same job id) never clashes with itself, so for an assigned job
 * both overlapping jobs are flagged.
 */
export function checkWorkerForJob(worker: ScheduleWorker, job: ScheduleJobRow, sameDayJobs: readonly ScheduleJobRow[]): CheckResult {
  if (job.isCompleted) {
    return COMPLETED
  }
  const status = dayStatusOf(worker, job.date)
  if (status.kind === 'leave') {
    return { ok: false, kind: 'leave', text: status.leaveType ? `On leave (${status.leaveType})` : 'On leave' }
  }
  if (status.kind === 'off') {
    return { ok: false, kind: 'off', text: 'Day off' }
  }

  const category = String(job.categoryLabel || '').trim()
  if (category && worker.categoriesKnown && !worker.anyCategory && !worker.categoryNames.some((name) => normalize(name) === normalize(category))) {
    return { ok: false, kind: 'category', text: `Not trained for ${category}` }
  }

  if (!hasWindow(job)) {
    return OK
  }

  if (status.kind === 'shift' && !status.ranges.some(([from, to]) => job.startMinute >= from && job.endMinute <= to)) {
    return { ok: false, kind: 'hours', text: `Outside shift (${status.label})` }
  }

  const clash = sameDayJobs.find((other) => (
    other.date === job.date
    && other.jobId !== job.jobId
    && hasWindow(other)
    && hasWorker(other, worker.id)
    && other.startMinute < job.endMinute
    && job.startMinute < other.endMinute
  ))
  if (clash && hasWindow(clash)) {
    return { ok: false, kind: 'clash', text: `Clash with ${clash.title} ${formatMinuteRange(clash.startMinute, clash.endMinute)}` }
  }

  return OK
}

export type Checker = (worker: ScheduleWorker, job: ScheduleJobRow) => CheckResult

/** `check(worker, job)` bound to every job of the window (indexed by day). */
export function createChecker(allJobs: readonly ScheduleJobRow[]): Checker {
  const byDay = new Map<string, ScheduleJobRow[]>()
  allJobs.forEach((job) => byDay.set(job.date, [...(byDay.get(job.date) ?? []), job]))
  return (worker, job) => checkWorkerForJob(worker, job, byDay.get(job.date) ?? [])
}

/** Placeholder for a worker id on a job that is not in the roster (no data: only the clash rule applies). */
export const unknownWorker = (id: string, name: string): ScheduleWorker => ({
  id,
  name,
  categoryNames: [],
  anyCategory: false,
  categoriesKnown: false,
  days: {},
})

export interface AssignmentProblem {
  worker: ScheduleWorker
  result: CheckResult
}

/** Assigned workers of `job` who fail `check` (each one is a problem with that assignment). */
export function findAssignmentProblems(job: ScheduleJobRow, workerById: ReadonlyMap<string, ScheduleWorker>, check: Checker): AssignmentProblem[] {
  return job.workers.flatMap((assigned) => {
    const worker = workerById.get(assigned.workerId) ?? unknownWorker(assigned.workerId, assigned.name)
    const result = check(worker, job)
    return result.ok ? [] : [{ worker, result }]
  })
}

export type IssueKind = 'open' | Exclude<CheckKind, 'ok'>

export interface JobIssue {
  kind: IssueKind
  /** e.g. "Anandh: On leave (Casual leave)" or "No worker assigned yet." */
  text: string
  problems: AssignmentProblem[]
  needsWorker: boolean
}

/** Null when the job is staffed with workers who can all take it. */
export function resolveJobIssue(job: ScheduleJobRow, workerById: ReadonlyMap<string, ScheduleWorker>, check: Checker): JobIssue | null {
  if (job.isCompleted) return null
  const problems = findAssignmentProblems(job, workerById, check)
  const needsWorker = isUnassignedRow(job)
  if (problems.length > 0) {
    const [first] = problems
    const more = problems.length > 1 ? ` (+${problems.length - 1} more)` : ''
    return { kind: first.result.kind as IssueKind, text: `${first.worker.name}: ${first.result.text}${more}`, problems, needsWorker }
  }
  if (needsWorker) {
    const open = job.openAssignmentIds.length
    const text = job.workers.length === 0
      ? 'No worker assigned yet.'
      : `${open} open slot${open === 1 ? '' : 's'} still need${open === 1 ? 's' : ''} a worker.`
    return { kind: 'open', text, problems, needsWorker }
  }
  return null
}

export const compareJobs = (left: ScheduleJobRow, right: ScheduleJobRow) => {
  if (left.date !== right.date) return left.date.localeCompare(right.date)
  if (left.startMinute == null || right.startMinute == null) {
    return left.startMinute == null ? (right.startMinute == null ? left.title.localeCompare(right.title) : 1) : -1
  }
  return left.startMinute - right.startMinute || left.title.localeCompare(right.title)
}

export interface AttentionItem {
  job: ScheduleJobRow
  issue: JobIssue
}

/** Every job that needs a worker or has a problem, by day then time. */
export function buildAttentionList(jobs: readonly ScheduleJobRow[], workerById: ReadonlyMap<string, ScheduleWorker>, check: Checker): AttentionItem[] {
  return jobs
    .flatMap((job) => {
      const issue = resolveJobIssue(job, workerById, check)
      return issue ? [{ job, issue }] : []
    })
    .sort((left, right) => compareJobs(left.job, right.job))
}

export interface Candidate {
  worker: ScheduleWorker
  result: CheckResult
  /** Already on this job. */
  isCurrent: boolean
  /** Their other jobs on the job's day. */
  load: number
}

/**
 * Workers split into free / can't take it. Free: current assignees first, then fewest other
 * jobs that day, then name. Can't take it keeps roster order.
 */
export function rankCandidates(job: ScheduleJobRow, workers: readonly ScheduleWorker[], allJobs: readonly ScheduleJobRow[], check: Checker) {
  const sameDay = allJobs.filter((other) => other.date === job.date && other.jobId !== job.jobId)
  const candidates: Candidate[] = workers.map((worker) => ({
    worker,
    result: check(worker, job),
    isCurrent: hasWorker(job, worker.id),
    load: sameDay.filter((other) => hasWorker(other, worker.id)).length,
  }))
  const free = candidates
    .filter((candidate) => candidate.result.ok)
    .sort((left, right) => Number(right.isCurrent) - Number(left.isCurrent) || left.load - right.load || left.worker.name.localeCompare(right.worker.name))
  const blocked = candidates.filter((candidate) => !candidate.result.ok)
  return { free, blocked }
}

/** Open jobs (needing a worker, not already on it) in `days`, split into fits / doesn't fit for `worker`. */
export function openJobsForWorker(worker: ScheduleWorker, jobs: readonly ScheduleJobRow[], days: readonly string[], check: Checker) {
  const daySet = new Set(days)
  const open = jobs
    .filter((job) => daySet.has(job.date) && isUnassignedRow(job) && !hasWorker(job, worker.id))
    .sort(compareJobs)
    .map((job) => ({ job, result: check(worker, job) }))
  return { fits: open.filter((item) => item.result.ok), noFit: open.filter((item) => !item.result.ok) }
}

/** The worker's own jobs (in `days`) that they fail `check` for. */
export function problemJobsForWorker(worker: ScheduleWorker, jobs: readonly ScheduleJobRow[], days: readonly string[], check: Checker) {
  const daySet = new Set(days)
  return jobs
    .filter((job) => daySet.has(job.date) && hasWorker(job, worker.id))
    .sort(compareJobs)
    .flatMap((job) => {
      const result = check(worker, job)
      return result.ok ? [] : [{ job, result }]
    })
}
